import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    Collapse,
    Container,
    Form,
    FormGroup,
    Input,
    Label,
    Pagination,
    PaginationItem,
    PaginationLink,
    Row,
    Table,
    UncontrolledTooltip
} from "reactstrap";

import { useTheme } from "../hooks/useTheme";
import { useSortData } from "../hooks/useSortData";
import { useSearchData } from "../hooks/useSearchData";
import { useFilterDataByField } from "../hooks/useFilterDataByField";

import DataLoaderFailed from "./loader/DataLoaderFailed";
import DataLoader from "./loader/DataLoader";
import SortIcon from "./SortIcon";
import DataFilter from "./DataFilter";
import Download from "./Download";
import { useLockState } from "./userLocks/LockCheck";

// this component does not query the database by page number - it will fetch all database records that meet the filter criteria.
// it should be used for queries returning only a few records
const DataTableNested = ({
    columns,
    data,
    isLoading,
    error,
    searchableFields = [],
    paging,
    onRowClick,
    filters = [],
    actions = [],
    exportToCsv = false,
    csvFileName = "data.csv",
    sortByColumn = null,
    sortDirection = "ascending",
    footer = null,
    onFilterChange,
    width = "100%",
    showNotesIcon
}) => {
    const theme = useTheme();

    // Advanced filters state and animations
    const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
    const mountedStyle = { animation: "inAnimation 250ms ease-in" };
    const unmountedStyle = { animation: "outAnimation 270ms ease-out", animationFillMode: "forwards" };

    // Search and sorting hooks
    const { sortedData, sortBy, sortConfig } = useSortData(data, { key: sortByColumn ? sortByColumn : columns[0]?.key, direction: sortDirection });
    const [searchFilteredData, setSearch] = useSearchData(sortedData, searchableFields);
    const [filteredData, setFilter] = useFilterDataByField(searchFilteredData);

    // Dynamic paging state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const numPerPageOptions = [10, 20, 50, 200];

    // locking state
    const lockState = useLockState();

    // Reset to first page when filteredData changes
    useEffect(() => {
        setCurrentPage(1);
    }, [filteredData.length]);

    // Calculate total pages based on filteredData length
    const totalPages = Math.ceil(filteredData.length / pageSize);

    // Determine data to display: slice the filteredData if paging is enabled
    const pagedData = paging
        ? filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize)
        : filteredData;

    // Handler for filter changes
    const handleFilterChange = (filter, value) => {
        if (value === undefined || value === null) {
            setFilter(filter.id, "");
        } else {
            setFilter(filter.id, value);
        }

        if (onFilterChange) onFilterChange({ filterId: filter.id, value: value });
    };

    // Handler for search input change
    const onSearchChanged = (e) => {
        setSearch(e.target.value);
    }

    // Handler for items per page change
    const handlePageSizeChange = (e) => {
        setPageSize(Number(e.target.value));
        setCurrentPage(1); // Reset to first page when items per page changes
    };

    const renderRow = (item, level = 0, parentKey = '') => {
        const hasChildren = Array.isArray(item.items) && item.items.length > 0;
        const rowKey = `${parentKey}-${item.id}`;
        const isExpanded = expandedRows.has(rowKey);
        console.log("item:", item);

        const row = (
            <tr
                key={rowKey}
                className={onRowClick ? "cursor-pointer" : ""}
                onClick={() => onRowClick?.(item)}
                style={onRowClick ? { cursor: "pointer" } : {}}
            >
                {/* GroupHeader will span any columns with groupColumn set to true */}
                {columns.filter(col => col.groupHeader && hasChildren).map((col, colIndex) => {
                    const value = item[col.key];

                    return (
                        <td
                            key={col.key}
                            className={col.className || ""}
                            style={col.style || {}}
                            colSpan={columns.filter(c => c.groupColumn).length || 1}
                        >
                            <div
                                className="d-flex align-items-center"
                                style={{ paddingLeft: `${colIndex === 0 ? level * 1.5 : 0}rem` }}
                            >
                                {colIndex === 0 && hasChildren && (
                                    <i
                                        className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRow(rowKey);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    />
                                )}
                                {col.render ? col.render(value, item, isExpanded, hasChildren) : value}
                            </div>
                        </td>
                    )
                })}
                {/* Group columns */}
                {columns.filter(col => col.groupColumn).map((col, colIndex) => {
                    if (hasChildren) return null; // Skip rendering group columns for header rows, as the header already is spanning the space that these take up

                    const value = item[col.key];
                    return (
                        <td
                            key={col.key}
                            className={col.className || ""}
                            style={col.style || {}}
                        >
                            <div
                                className="d-flex align-items-center"
                                style={{ paddingLeft: `${colIndex === 0 ? level * 1.5 : 0}rem` }}
                            >
                                {col.render ? col.render(value, item, isExpanded, hasChildren) : value}
                            </div>
                        </td>
                    );
                })}
                {/* Non-group columns */}
                {columns.filter(col => !col.groupHeader && !col.groupColumn).map((col, colIndex) => {
                    const value = item[col.key];

                    return (
                        <td
                            key={col.key}
                            className={col.className || ""}
                            style={col.style || {}}
                        >
                            <div
                                className={`d-flex align-items-center ${lockState?.isLocked && (level === 0 && !hasChildren) ? "locked" : ""}`}
                                style={{
                                    paddingLeft: `${colIndex === 0 ? level * 1.5 : 0}rem`,
                                    justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                    width: "100%"
                                }}
                            >
                                {colIndex === 0 && hasChildren && columns.filter(col => col.groupHeader).length === 0 && (
                                    <i
                                        className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"} me-2`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleRow(rowKey);
                                        }}
                                        style={{ cursor: "pointer" }}
                                    />
                                )}
                                {col.render ? col.render(value, item, isExpanded, hasChildren) : value}
                            </div>
                        </td>
                    );
                })}
            </tr>
        );

        const children = hasChildren && isExpanded
            ? item.items.flatMap(child => renderRow(child, level + 1, rowKey))
            : [];

        return [row, ...children];
    };




    //nested
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [animatingRows, setAnimatingRows] = useState({});
    const [expandCollapseButtonState, setExpandCollapseButtonState] = useState("expand all"); // null, 'expanding', 'collapsing'

    const setAllRowsExpandedState = (setToExpand) => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);

            for (const id of data.map(item => item.id)) {
                //The code expects the id to be parentid then hyphen then id of the item, but, as this is a top level row, we just add a hyphen before the id
                const rowKey = `-${id}`;

                if (setToExpand && !newSet.has(rowKey)) {
                    newSet.add(rowKey); // 
                } else if (!setToExpand && newSet.has(rowKey)) {
                    newSet.delete(rowKey);
                }
            }
            return newSet;
        });
    };

    const removeExpandedRowsFromStateThatDontExistAnymore = () => {
        setExpandedRows((prev) => {
            const newSet = new Set(prev);
            for (const id of prev) {
                // we prefix a hyphen to the id, as the code expects the format to be parentid then hyphen then id of the item, but,
                // as this is a top level row, we just add a hyphen before the id
                if (!data.find(item => "-" + item.id === id)) {
                    console.log("removing expanded row from state as it no longer exists in data: " + id);
                    newSet.delete(id);
                }
            }
            return newSet;
        });
    };


    const toggleRow = (id) => {
        const isCurrentlyExpanded = expandedRows.has(id);
        setAnimatingRows((prev) => ({ ...prev, [id]: isCurrentlyExpanded ? 'collapsing' : 'expanding' }));

        setTimeout(() => {
            setExpandedRows((prev) => {
                const newSet = new Set(prev);
                isCurrentlyExpanded ? newSet.delete(id) : newSet.add(id);
                return newSet;
            });
            setAnimatingRows((prev) => {
                const updated = { ...prev };
                delete updated[id];
                return updated;
            });
        }, 300); // matches CSS transition
    };

    //pagination
    const getPaginationArray = (currentPage, totalPages) => {
        const paginationArray = [];

        if (totalPages <= 5) {
            // Show all pages if 5 or fewer
            for (let i = 1; i <= totalPages; i++) {
                paginationArray.push(i);
            }
        } else {
            if (currentPage <= 3) {
                // Start of the range
                paginationArray.push(1, 2, 3, 4);
                paginationArray.push("ellipsis");
                paginationArray.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                // End of the range
                paginationArray.push(1);
                paginationArray.push("ellipsis");
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    paginationArray.push(i);
                }
            } else {
                // Middle of the range
                paginationArray.push(1);
                paginationArray.push("ellipsis");
                paginationArray.push(currentPage, currentPage + 1, currentPage + 2);
                paginationArray.push("ellipsis");
                paginationArray.push(totalPages);
            }
        }

        return paginationArray;
    };

    if (error) return <DataLoaderFailed />;
    if (isLoading) return <DataLoader />;

    return (
        <>
            {/* Search Bar */}
            {(searchableFields.length > 0 || actions.length > 0) && (
                <Container fluid className="px-0">
                    <div
                        className="d-flex justify-content-between align-items-center flex-wrap gap-3"
                        style={{ minHeight: "40px" }}
                    >
                        {searchableFields.length > 0 && (
                            <Form
                                style={{ flex: "1 1 auto", minWidth: "250px" }}
                                onSubmit={(e) => e.preventDefault()}
                            >
                                <Label className="visually-hidden" htmlFor="search">
                                    Search
                                </Label>
                                <Input
                                    type="search"
                                    id="search"
                                    placeholder={`Search ${filteredData.length} records`}
                                    onChange={onSearchChanged}
                                    style={{ height: "38px" }}
                                />
                            </Form>
                        )}


                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            {filters
                                .filter((filter) => !filter.advanced)
                                .map((filter, index) => {
                                    return (
                                        <DataFilter
                                            key={index}
                                            id={filter.id}
                                            label={filter.label}
                                            type={filter.type}
                                            placeholder={filter.placeholder}
                                            isSearchable={filter.isSearchable}
                                            options={filter.options}
                                            value={filter.type !== "select" ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                            onChange={(value) => handleFilterChange(filter, value)}
                                            isInvalid={filter.isInvalid}
                                            defaultValue={filter.defaultValue}
                                        />)
                                }
                                )
                            }
                        </div>

                        {/* Advanced Filters Toggle */}
                        {filters.filter((filter) => filter.advanced && !filter.isDateTimeRange).length > 0 && (
                            <Button
                                outline={!advancedFiltersOpen}
                                color={theme === "dark-mode" ? "dark" : "light"}
                                onClick={() => setAdvancedFiltersOpen(!advancedFiltersOpen)}
                            >
                                <i className="bi bi-funnel"></i>
                            </Button>
                        )}

                        <div style={{ flexGrow: 1 }}></div>

                        {/* Action Buttons */}
                        <div className="d-flex gap-2">
                            {actions.map((action, index) => (
                                <div key={index}>{action}</div>
                            ))}
                        </div>
                        {exportToCsv && (<Download fileName={csvFileName} data={filteredData} showIcon={false} />)}
                    </div>
                </Container>
            )}

            {/* Advanced Filters */}
            {advancedFiltersOpen && (
                <>
                    <br />
                    <Row>
                        <Container
                            fluid
                            style={advancedFiltersOpen ? mountedStyle : unmountedStyle}
                            className="contrast-dropdown py-3 transitionDiv"
                        >
                            <div style={{ minHeight: "40px" }}>
                                {filters
                                    .filter((filter) => filter.advanced)
                                    .map((filter, index) => {
                                        return (
                                            <DataFilter
                                                key={index}
                                                id={filter.id}
                                                label={filter.label}
                                                type={filter.type}
                                                placeholder={filter.placeholder}
                                                isSearchable={filter.isSearchable}
                                                options={filter.options}
                                                value={filter.type !== "select" ? filter.value : filter.options.find((opt) => opt.value === filter.value) || null}
                                                onChange={(value) => handleFilterChange(filter, value)}
                                                isInvalid={filter.isInvalid}
                                                defaultValue={filter.defaultValue}
                                                advanced={true}
                                            />
                                        )
                                    }
                                    )}
                            </div>
                        </Container>
                    </Row>
                </>
            )}

            <Container fluid className="px-0">
                <Table
                    className={
                        theme === "dark-mode"
                            ? "no-wrap mt-3 align-middle table-dark"
                            : "no-wrap mt-3 align-middle"
                    }
                    hover
                    responsive
                    striped
                    style={{ width: width }}
                >
                    <thead>
                        <tr>
                            {columns.filter(x => !x.groupHeader).map((col, index) => (
                                <th key={col.key}
                                    style={col.isSortDisabled ? { paddingLeft: "0" } : { cursor: "pointer", ...(col.style || {}), paddingLeft: "0" }}
                                    onClick={() => !col.isSortDisabled && sortBy(col.key)}>
                                    <span style={{
                                        display: "flex",
                                        justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                                        textAlign: col.align === "right" ? "right" : "left", // This is needed to ensure the header text aligns properly if the content wraps
                                        width: "100%"
                                    }}>
                                        {index === 0 && expandCollapseButtonState === "expand all" && (
                                            <i
                                                className={`bi ${"bi-chevron-double-down"} mx-2`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAllRowsExpandedState(true);
                                                    setExpandCollapseButtonState("collapse all");
                                                }}
                                            />
                                        )}
                                        {index === 0 && expandCollapseButtonState === "collapse all" && (
                                            <i
                                                className={`bi ${"bi-chevron-double-up"} mx-2`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setAllRowsExpandedState(false);
                                                    setExpandCollapseButtonState("expand all");
                                                }}
                                            />
                                        )}
                                        <span className={col.align === "left" ? "ms-2" : ""}>{col.header ?? col.label}</span>
                                        &nbsp;
                                        {sortConfig?.key === col.key && !col.isSortDisabled && <SortIcon sort={sortConfig?.direction} />}
                                        {col.isSortDisabled && <i style={{ opacity: 0.2 }} className="bi bi-chevron-expand"></i>}
                                    </span>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {pagedData.flatMap((item) => renderRow(item))}
                    </tbody>
                    {footer && <tfoot>{footer}</tfoot>}
                </Table>

                {/* Pagination Controls */}
                {paging && (
                    <Row className="mt-3 unlocked">
                        <Col className="d-flex justify-content-start">
                            <Pagination aria-label="Page navigation">
                                <PaginationItem disabled={currentPage === 1}>
                                    <PaginationLink
                                        first
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentPage(1);
                                        }}
                                    />
                                </PaginationItem>
                                <PaginationItem disabled={currentPage === 1}>
                                    <PaginationLink
                                        previous
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentPage(currentPage - 1);
                                        }}
                                    />
                                </PaginationItem>
                                {(() => {
                                    const pages = [];

                                    const createPage = (pageNum) => (
                                        <PaginationItem key={pageNum} active={currentPage === pageNum}>
                                            <PaginationLink
                                                href="#"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    setCurrentPage(pageNum);
                                                }}
                                            >
                                                {pageNum}
                                            </PaginationLink>
                                        </PaginationItem>
                                    );

                                    const lastPage = totalPages;

                                    if (lastPage <= 4) {
                                        // Just show all pages
                                        for (let i = 1; i <= lastPage; i++) {
                                            pages.push(createPage(i));
                                        }
                                    } else {
                                        let startPage = currentPage;

                                        if (startPage > lastPage - 3) {
                                            // Clamp to last 4 pages
                                            startPage = lastPage - 3;
                                        }

                                        const visiblePages = [];
                                        for (let i = startPage; i < startPage + 3; i++) {
                                            visiblePages.push(i);
                                        }

                                        visiblePages.forEach((page) => pages.push(createPage(page)));

                                        if (startPage + 3 <= lastPage) {
                                            // Show final page or ellipsis
                                            if (startPage + 3 === lastPage) {
                                                pages.push(createPage(lastPage));
                                            } else {
                                                pages.push(
                                                    <PaginationItem key="ellipsis" disabled>
                                                        <PaginationLink href="#">...</PaginationLink>
                                                    </PaginationItem>
                                                );
                                            }
                                        }
                                    }

                                    return pages;
                                })()}
                                <PaginationItem disabled={currentPage === totalPages}>
                                    <PaginationLink
                                        next
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentPage(currentPage + 1);
                                        }}
                                    />
                                </PaginationItem>
                                <PaginationItem disabled={currentPage === totalPages}>
                                    <PaginationLink
                                        last
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setCurrentPage(totalPages);
                                        }}
                                    />
                                </PaginationItem>
                            </Pagination>
                            <div className="ps-3 p-2 d-flex justify-content-end">
                                <span className="text-muted">
                                    {totalPages > 0 ? `Page ${currentPage} of ${totalPages}` : 'No records found'}
                                </span>
                            </div>
                        </Col>
                        <Col className="d-flex justify-content-end">
                            <Input
                                id="pageSizeSelect"
                                name="select"
                                type="select"
                                value={pageSize}
                                style={{ width: "5rem", height: "3rem" }}
                                bsSize="sm"
                                onChange={handlePageSizeChange}
                            >
                                {numPerPageOptions.map((num, index) => (
                                    <option key={index} value={num}>
                                        {num}
                                    </option>
                                ))}
                            </Input>
                        </Col>
                    </Row>
                )}
            </Container>
        </>
    );
};

export default DataTableNested;
